import React from 'react';
import { useFormContext, FieldValues, Path, PathValue } from 'react-hook-form';
import PathControlledDropdown, {
  ControlledDropdown
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/PathControlledDropdown';
import PathControlledNumberInput from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/PathControlledNumberInput';
import { OperatorTypeSchema, ThresholdUnitSchema } from '../schema/AlertBuilderSchema';
import { OperatorConfigType, OperatorType, RuleBlockConfigType } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchemaTypes';
import { Box } from '@mui/material';

import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { getOperatorConstraints, DROPDOWN_WIDTH } from '../utils';

type Props<T extends FieldValues> = {
  groupIndex: number;
  operatorIndex: number;
};

/**
 * A specialized dropdown for rule block operators that manages the complete operator object structure
 */
function RuleBlockOperatorDropdown<T extends FieldValues>({
  groupIndex,
  operatorIndex
}: Props<T>) {
  const { setValue, watch } = useFormContext<T>();

  // Paths for the operator
  const operatorsPath = `queryJSON.rules.ruleGroups.${groupIndex}.operators` as Path<T>;
  const ruleBlocksPath = `queryJSON.rules.ruleGroups.${groupIndex}.ruleBlocks` as Path<T>;

  // Watch the current operators array, rule blocks, and specific operator
  const operators = (watch(operatorsPath) || []) as OperatorConfigType[];
  const ruleBlocks = (watch(ruleBlocksPath) || []) as RuleBlockConfigType[];
  const currentOperator = operators[operatorIndex];

  // Get operator constraints using pure function
  const { availableOptions, constraintInfo, getCascadedOperators } = getOperatorConstraints(operators, operatorIndex);

  // Handle operator type change
  const handleOperatorChange = (newType: OperatorType) => {
    // Create or update the complete operator object
    const updatedOperator: OperatorConfigType = {
      type: newType
    };

    // If operator requires threshold, initialize defaults; otherwise ensure threshold is removed
    const needsThreshold =
      newType === OperatorTypeSchema.enum['greater than'] || newType === OperatorTypeSchema.enum['less than'];
    if (needsThreshold) {
      updatedOperator.threshold = { value: 0, unit: ThresholdUnitSchema.options[0] };
    }

    // Get cascaded operators (includes the current change + any cascading updates)
    const updatedOperators = getCascadedOperators(currentOperator, newType, updatedOperator);

    // Batch update: set operators once with all changes
    setValue(operatorsPath, updatedOperators as PathValue<T, typeof operatorsPath>);
  };

  const needsThreshold =
    currentOperator?.type === OperatorTypeSchema.enum['greater than'] ||
    currentOperator?.type === OperatorTypeSchema.enum['less than'];

  const thresholdValuePath =
    `queryJSON.rules.ruleGroups.${groupIndex}.operators.${operatorIndex}.threshold.value` as Path<T>;
  const thresholdUnitPath =
    `queryJSON.rules.ruleGroups.${groupIndex}.operators.${operatorIndex}.threshold.unit` as Path<T>;

  return (
    <Flex gap={16} alignItems="center">
      <ControlledDropdown
        label=""
        value={currentOperator?.type || 'AND'}
        onChange={handleOperatorChange}
        options={availableOptions}
        placeholder="AND"
        sx={{ width: '150px' }}
      />

      {needsThreshold && (
        <>
          <Text variant="body3" sx={{ fontSize: '14px' }}>
            by
          </Text>
          <Flex alignItems="center">
            <Box sx={{ width: '100px' }}>
              <PathControlledNumberInput name={thresholdValuePath} label="" min={0} max={999999} />
            </Box>
            <Box sx={{ width: '100px', marginLeft: '-8px' }}>
              <PathControlledDropdown
                name={thresholdUnitPath}
                label=""
                options={ThresholdUnitSchema.options}
                placeholder="Unit"
              />
            </Box>
          </Flex>
          <Text variant="body3" sx={{ fontSize: '14px' }}>
            compared to the following rule:
          </Text>
        </>
      )}
    </Flex>
  );
}

export default RuleBlockOperatorDropdown;
